import { useState } from 'react';
import { ErrorBanner, Modal } from '../../components/ui';
import { downloadCsv, fileStamp, slugForFile } from '../../lib/download';
import { readSpreadsheet } from '../../lib/spreadsheet';
import type { Sheet } from '../../lib/spreadsheet';
import type { AmAuditAnswer, AmAuditDetail } from '../../lib/types';

/**
 * An audit, out to a spreadsheet and back.
 *
 * WHY A ROUND TRIP AT ALL. An audit is answered by whoever was on site, and
 * that is not always somebody sitting in this app — it is an engineer with a
 * laptop in a server room, or a subcontractor who will never have a login.
 * Handing them the questions as a file and reading their answers back is the
 * difference between an audit that gets done and one that gets retyped.
 *
 * THE EXPORT CARRIES THE QUESTION ID, and the import matches on it. That is
 * what makes the round trip exact: a question can be reworded between the
 * export and the import and the answer still lands on the right one. A file
 * somebody built by hand has no ids, so the import falls back to matching the
 * question text within its category — forgiving when it has to be, exact when
 * it can be.
 *
 * NOTHING IS SAVED BY THE IMPORT. The answers land in the editor as unsaved
 * changes for somebody to look at and then save, the same as if they had been
 * typed. An import that writes straight to the database is one that cannot be
 * reviewed, and this is a document a client is shown.
 */

export const AUDIT_COLUMNS = ['Question ID', 'Category', 'Question', 'Response', 'Risk', 'Notes'];

const RISKS = ['Low', 'Medium', 'High', 'Critical'];

/** Case, spaces and punctuation are not part of a heading's identity. */
const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** One row per question, answered or not. */
export function exportAudit(audit: AmAuditDetail, answers: AmAuditAnswer[]): void {
  const name = audit.Name || audit.SetName || 'audit';
  downloadCsv(`${slugForFile(`${audit.ClientName} ${name}`)}_${fileStamp()}.csv`, [
    AUDIT_COLUMNS,
    ...answers.map((a) => [
      a.AmQuestionId,
      a.Category ?? '',
      a.Text,
      a.Response ?? '',
      a.Risk ?? '',
      a.Notes ?? '',
    ]),
  ]);
}

export interface AuditDraft {
  response: string;
  risk: string;
  notes: string;
}

/** A question a row can land on. Answered or not, existing audit or template. */
export interface AuditTarget {
  AmQuestionId: string;
  Category: string | null;
  Text: string;
  Response?: string | null;
  Risk?: string | null;
  Notes?: string | null;
}

export interface Match {
  questionId: string;
  question: string;
  draft: AuditDraft;
  /** Whether this differs from what the audit already holds. */
  changed: boolean;
  /** A cell that could not be used, said before anything is applied. */
  note?: string;
}

/**
 * Match a read sheet onto a set of questions.
 *
 * Shared by both ways in — a file read into an audit that exists, and a file
 * read while a new audit is being created — so the two cannot disagree about
 * what a row means. Throws when the file is the wrong shape entirely, which
 * is a thing to say once rather than a hundred unmatched rows.
 */
export function matchAuditSheet(
  sheet: Sheet,
  targets: AuditTarget[],
): { matches: Match[]; unmatched: string[] } {
  const at = (name: string) => sheet.headers.findIndex((h) => key(h) === key(name));
  const idAt = at('Question ID');
  const categoryAt = at('Category');
  const questionAt = at('Question');
  const responseAt = at('Response');
  const riskAt = at('Risk');
  const notesAt = at('Notes');

  if (idAt === -1 && questionAt === -1) {
    throw new Error(
      'That file has neither a Question ID nor a Question column — export an audit first to get the right shape.',
    );
  }
  if (responseAt === -1 && riskAt === -1 && notesAt === -1) {
    throw new Error('That file has no Response, Risk or Notes column, so there is nothing to import.');
  }

  const byId = new Map(targets.map((t) => [t.AmQuestionId, t]));
  /* Text within its category, so two sections asking "Is MFA enforced?" do
     not collide. */
  const byText = new Map(targets.map((t) => [`${key(t.Category ?? '')}|${key(t.Text)}`, t]));

  const matches: Match[] = [];
  const unmatched: string[] = [];
  const seen = new Set<string>();

  for (const cells of sheet.rows) {
    const cell = (i: number) => (i === -1 ? '' : (cells[i] ?? '').trim());
    const id = cell(idAt);
    const question = cell(questionAt);
    if (!id && !question) continue;

    const target =
      (id ? byId.get(id) : undefined) ?? byText.get(`${key(cell(categoryAt))}|${key(question)}`) ?? null;
    if (!target) {
      unmatched.push(question || id);
      continue;
    }
    /* The first row wins. A file with the same question twice is somebody's
       copy-and-paste, and silently taking the last one hides it. */
    if (seen.has(target.AmQuestionId)) {
      unmatched.push(`${question || id} (already matched above)`);
      continue;
    }
    seen.add(target.AmQuestionId);

    const rawRisk = cell(riskAt);
    const risk = RISKS.find((r) => r.toLowerCase() === rawRisk.toLowerCase()) ?? '';
    const draft: AuditDraft = { response: cell(responseAt), risk, notes: cell(notesAt) };
    matches.push({
      questionId: target.AmQuestionId,
      question: target.Text,
      draft,
      changed:
        draft.response !== (target.Response ?? '') ||
        draft.risk !== (target.Risk ?? '') ||
        draft.notes !== (target.Notes ?? ''),
      note: rawRisk && !risk ? `"${rawRisk}" is not a risk level — left blank` : undefined,
    });
  }

  return { matches, unmatched };
}

export function AmAuditImport({
  answers,
  onClose,
  onApply,
}: {
  /** Every question in the audit, with what it currently holds. */
  answers: AmAuditAnswer[];
  onClose: () => void;
  onApply: (drafts: Record<string, AuditDraft>) => void;
}) {
  const [matches, setMatches] = useState<Match[] | null>(null);
  const [unmatched, setUnmatched] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const sheet = await readSpreadsheet(file);
      const out = matchAuditSheet(sheet, answers);
      setFileName(file.name);
      setMatches(out.matches);
      setUnmatched(out.unmatched);
    } catch (e) {
      setMatches(null);
      setError(e instanceof Error ? e.message : 'Could not read that file');
    } finally {
      setBusy(false);
    }
  };
  const changed = matches?.filter((m) => m.changed) ?? [];
  const noted = matches?.filter((m) => m.note) ?? [];

  const apply = () => {
    if (!matches) return;
    const drafts: Record<string, AuditDraft> = {};
    for (const m of matches) drafts[m.questionId] = m.draft;
    onApply(drafts);
  };

  return (
    <Modal
      title="Import answers from a spreadsheet"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          {changed.length > 0 && (
            <button type="button" className="btn btn-primary" onClick={apply}>
              Apply {changed.length} answer{changed.length === 1 ? '' : 's'}
            </button>
          )}
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="small">
        Export the audit, fill the Response, Risk and Notes columns in Excel, and read it back here.
        Rows are matched on <strong>Question ID</strong>, so a question can be reworded in between
        and the answer still lands in the right place. .csv or .xlsx.
      </p>
      <p className="xs muted mt-2">
        Nothing is saved by this — the answers arrive as unsaved changes for you to look over, and
        the audit's own Save button is what writes them.
      </p>

      <div className="row wrap mt-4" style={{ gap: 'var(--space-3)' }}>
        <label className="btn btn-secondary btn-sm">
          {matches ? 'Choose a different file' : 'Choose a file'}
          <input
            type="file"
            accept=".csv,.xlsx"
            style={{ display: 'none' }}
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        {busy && <span className="xs muted">Reading…</span>}
      </div>

      {matches && (
        <div className="mt-4">
          <p className="small">
            <strong>{fileName}</strong> — {matches.length} row{matches.length === 1 ? '' : 's'}{' '}
            matched a question, {changed.length} of them change an answer
            {unmatched.length > 0 ? `, ${unmatched.length} matched nothing` : ''}
            {noted.length > 0 ? `, ${noted.length} with a cell to check` : ''}.
          </p>
          {changed.length === 0 && matches.length > 0 && (
            <p className="xs muted mt-2">
              Every answer in that file is already what the audit holds, so there is nothing to
              apply.
            </p>
          )}

          {(unmatched.length > 0 || noted.length > 0) && (
            <div className="table-wrap mt-2">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '45%' }}>Row</th>
                    <th>What will happen</th>
                  </tr>
                </thead>
                <tbody>
                  {unmatched.slice(0, 50).map((u, i) => (
                    <tr key={`u${i}`}>
                      <td className="xs">{u || <span className="subtle">—</span>}</td>
                      <td className="xs">No question in this audit matches it — skipped</td>
                    </tr>
                  ))}
                  {noted.map((m) => (
                    <tr key={m.questionId}>
                      <td className="xs">{m.question}</td>
                      <td className="xs">{m.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
